import { Link } from "react-router-dom";
import PropTypes from 'prop-types';

const TeamDetailComponent = (props) => {
  const {
    image,
    authorLink,
    authorTitle,
    paragraph1,
    paragraph2,
  } = props.person;

  return (
    <div className="min-h-screen bg-background-dark text-slate-100 font-display">
      {/* Hero Section */}
      <section className="relative pt-32 sm:pt-36 lg:pt-48 pb-12 sm:pb-16 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-accent/5"></div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-8 sm:mb-16">
            <div className="inline-flex items-center space-x-2 bg-accent/10 border border-accent/20 text-accent px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-semibold mb-6 sm:mb-8">
              <span className="w-2 h-2 bg-accent rounded-full"></span>
              <span>Software Engineer & Nutrition Coach</span>
              <span className="w-2 h-2 bg-accent rounded-full"></span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-7xl font-bold text-slate-100 mb-4 sm:mb-6 leading-tight">
              About Andy Cary
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-slate-400 max-w-3xl mx-auto leading-relaxed px-4">
              Building the future through code, coaching, and community
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 sm:py-12 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Image Section */}
            <div className="relative">
              <div className="relative group">
                <div className="absolute -inset-4 bg-accent/30 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
                <div className="relative">
                  <img 
                    src={image} 
                    alt="Andy Cary" 
                    className="w-full h-auto rounded-2xl shadow-2xl transform group-hover:scale-105 transition duration-500"
                  />
                </div>
              </div>
              
              {/* Friendly Caption */}
              <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-slate-800/50 rounded-xl shadow-xl p-3 sm:p-4 transform rotate-3 max-w-[200px] sm:max-w-xs border border-accent/10">
                <div className="text-center">
                  <div className="text-xs sm:text-sm font-semibold text-slate-100">Senior Software Engineer</div>
                  <div className="text-xs text-slate-400">at The Home Depot</div>
                  <div className="text-xs text-accent mt-1">Master's in Engineering</div>
                  <div className="text-xs text-slate-500">from UT Austin</div>
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="space-y-6 sm:space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-slate-100 mb-4 sm:mb-6">
                  Andrew (Andy) Cary
                </h2>
                <div className="flex items-center space-x-4 mb-6 sm:mb-8">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-accent rounded-full flex items-center justify-center">
                    <span className="text-background-dark font-bold text-base sm:text-lg">A</span>
                  </div>
                  <div>
                    <div className="text-slate-100 font-semibold text-sm sm:text-base">Senior Software Engineer</div>
                    <div className="text-slate-400 text-sm sm:text-base">The Home Depot</div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 sm:space-y-6">
                <p className="text-base sm:text-lg text-slate-400 leading-relaxed">
                  {paragraph1}
                </p>
                <p className="text-base sm:text-lg text-slate-400 leading-relaxed">
                  {paragraph2}
                </p>
              </div>

              {/* Education, Experience & Certifications */}
              <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
                <div className="bg-slate-800/30 rounded-xl p-4 sm:p-6 border border-accent/10">
                  <h3 className="text-lg sm:text-xl font-semibold text-slate-100 mb-3 sm:mb-4">Education</h3>
                  <div className="space-y-2 sm:space-y-3">
                    <div>
                      <div className="text-accent font-medium text-sm sm:text-base">MS, Engineering</div>
                      <div className="text-slate-400 text-sm sm:text-base">UT Austin, Cockrell School</div>
                    </div>
                    <div>
                      <div className="text-accent font-medium text-sm sm:text-base">BS, Information Science</div>
                      <div className="text-slate-400 text-sm sm:text-base">UNC Chapel Hill</div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/30 rounded-xl p-4 sm:p-6 border border-accent/10">
                  <h3 className="text-lg sm:text-xl font-semibold text-slate-100 mb-3 sm:mb-4">Experience</h3>
                  <div className="space-y-2 sm:space-y-3">
                    <div>
                      <div className="text-accent font-medium text-sm sm:text-base">Sr. Software Engineer</div>
                      <div className="text-slate-400 text-sm sm:text-base">The Home Depot</div>
                    </div>
                    <div>
                      <div className="text-accent font-medium text-sm sm:text-base">Software Engineer</div>
                      <div className="text-slate-400 text-sm sm:text-base">REI Systems, Inc.</div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/30 rounded-xl p-4 sm:p-6 border border-accent/10">
                  <h3 className="text-lg sm:text-xl font-semibold text-slate-100 mb-3 sm:mb-4">Certifications</h3>
                  <div className="space-y-2 sm:space-y-3">
                    <div>
                      <div className="text-accent font-medium text-sm sm:text-base">Generative AI Professional</div>
                      <div className="text-slate-400 text-sm sm:text-base">Oracle Cloud Infrastructure, 2025</div>
                    </div>
                    <div>
                      <div className="text-accent font-medium text-sm sm:text-base">AI Foundations Associate</div>
                      <div className="text-slate-400 text-sm sm:text-base">Oracle Cloud Infrastructure, 2025</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <a
                  href="https://www.linkedin.com/in/andrewdcary/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <div className="bg-accent hover:bg-accent/90 text-background-dark px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform group-hover:scale-105 group-hover:shadow-lg">
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      <span>LinkedIn</span>
                    </div>
                  </div>
                </a>

                <a
                  href="https://github.com/acary"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <div className="bg-slate-800/50 border border-accent/20 hover:border-accent/30 text-slate-100 px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform group-hover:scale-105 group-hover:shadow-lg">
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                      </svg>
                      <span>GitHub</span>
                    </div>
                  </div>
                </a>

                <a
                  href={authorLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <div className="bg-accent/20 border border-accent/30 hover:bg-accent/30 text-accent px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform group-hover:scale-105 group-hover:shadow-lg">
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                      </svg>
                      <span>{authorTitle}</span>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-8 sm:py-12 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center">
            <div className="bg-accent/5 rounded-3xl p-6 sm:p-8 lg:p-12 border border-accent/10">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-100 mb-4 sm:mb-6">
                Let's build the future together
              </h3>
              <p className="text-lg sm:text-xl text-slate-400 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
                Ready to collaborate on your next project? Whether it's software development or nutrition coaching, I'm here to help you achieve your goals.
              </p>
              <Link 
                to="/contact" 
                className="inline-flex items-center space-x-2 bg-accent text-background-dark hover:bg-accent/90 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
              >
                <span>Get in Touch</span>
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

TeamDetailComponent.propTypes = {
  person: PropTypes.shape({
    image: PropTypes.string.isRequired,
    authorLink: PropTypes.string.isRequired,
    authorTitle: PropTypes.string.isRequired,
    authorDescription: PropTypes.string.isRequired,
    paragraph1: PropTypes.string.isRequired,
    paragraph2: PropTypes.string.isRequired
  }).isRequired
};

export default TeamDetailComponent;
